import { useState } from "react";
import { useConversationContext } from "../context/ConversationContext";
import {
  useGetAllConversationsMutation,
  useGetSingleConversationMutation,
} from "./use-whatsapp-queries";
import { toSeconds } from "../utils/handle-messages";
import { QueryType } from "../types";

interface ToastState {
  open: boolean;
  message: string;
  variant: "default" | "destructive" | "success";
}

export function useWhatsapp(fundName: string) {
  const [queryType, setQueryType] = useState<QueryType>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [opsName, setOpsName] = useState("");
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [hasConversation, setHasConversation] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    variant: "default",
  });

  const { setConversations, setConversation } = useConversationContext();

  const getAllConversationsMutation = useGetAllConversationsMutation();
  const getSingleConversationMutation = useGetSingleConversationMutation();

  const handleSelectAll = () => {
    setQueryType("all");
    setHasConversation(false);
  };

  const handleSelectSingle = () => {
    setQueryType("single");
    setHasConversation(false);
  };

  const handleLast30Minutes = () => {
    const now = new Date();
    const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

    const nowAdjusted = new Date(now.getTime() - 3 * 60 * 60 * 1000);
    const thirtyMinutesAgoAdjusted = new Date(
      thirtyMinutesAgo.getTime() - 3 * 60 * 60 * 1000
    );

    setEndDate(nowAdjusted);
    setStartDate(thirtyMinutesAgoAdjusted);
  };

  const handleSubmitAllConversations = async () => {
    if (!opsName) {
      setToast({
        open: true,
        message: "Por favor, selecione uma operação",
        variant: "destructive",
      });
      return;
    }

    try {
      const conversations = await getAllConversationsMutation.mutateAsync({
        clientName: fundName,
        ops: opsName,
        start: toSeconds(startDate),
        end: toSeconds(endDate),
      });

      setConversations(conversations);
      setHasConversation(true);
      setToast({
        open: true,
        message: `${conversations.length} conversas encontradas`,
        variant: "success",
      });
    } catch (error) {
      setToast({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Erro ao buscar conversas. Tente novamente.",
        variant: "destructive",
      });
      console.error("handleSubmitAllConversations error:", error);
    }
  };

  const handleSubmitSingleConversation = async () => {
    if (!opsName) {
      setToast({
        open: true,
        message: "Por favor, selecione uma operação",
        variant: "destructive",
      });
      return;
    }

    if (!phoneNumber) {
      setToast({
        open: true,
        message: "Por favor, insira um número de telefone",
        variant: "destructive",
      });
      return;
    }

    try {
      const cleanedPhoneNumber = phoneNumber.replace(/\D/g, "");

      const conversation = await getSingleConversationMutation.mutateAsync({
        clientName: fundName,
        phone: cleanedPhoneNumber,
        ops: opsName,
        start: toSeconds(startDate),
        end: toSeconds(endDate),
        dbName: fundName,
      });

      setConversation(conversation);
      setHasConversation(true);
      setToast({
        open: true,
        message: "Conversa carregada com sucesso",
        variant: "success",
      });
    } catch (error) {
      setToast({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Erro ao buscar conversa. Tente novamente.",
        variant: "destructive",
      });
      console.error("handleSubmitSingleConversation error:", error);
    }
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  return {
    queryType,
    phoneNumber,
    opsName,
    startDate,
    endDate,
    hasConversation,
    toast,
    isLoading:
      getAllConversationsMutation.isPending ||
      getSingleConversationMutation.isPending,
    setPhoneNumber,
    setOpsName,
    setStartDate,
    setEndDate,
    handleSelectAll,
    handleSelectSingle,
    handleLast30Minutes,
    handleSubmitAllConversations,
    handleSubmitSingleConversation,
    closeToast,
  };
}
